<!-- Hero: image left, panel right — mirrored from /sell to signal the "reverse" -->
<section class="bg-forest">
  <div class="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2">
    <div class="order-last aspect-[4/5] overflow-hidden lg:order-first lg:aspect-auto">
      <img
        src="assets/marketing/wanted-hero.webp"
        alt="A western saddle and coiled rope, waiting"
        fetchpriority="high"
        class="h-full w-full object-cover"
      />
    </div>

    <div class="flex items-center px-4 py-16 sm:px-6 sm:py-20 lg:px-12 lg:py-0 xl:px-16">
      <div class="max-w-md">
        <p class="text-sm font-medium uppercase tracking-wide text-gold">Reverse the search</p>
        <h1 class="mt-4 font-display text-4xl font-semibold text-cream sm:text-5xl">
          Say what you want. Let it come to you.
        </h1>
        <p class="mt-5 text-lg text-cream/75">
          Post a Wanted Saddle — brand, discipline, size, tree width, budget — and sellers with a
          matching saddle already listed can respond directly. No endless browsing.
        </p>

        <div class="mt-9 flex flex-wrap items-center gap-4">
          <button
            type="button"
            (click)="startWantedAd()"
            class="cursor-pointer rounded-full bg-gold px-6 py-3 text-sm font-medium text-ink transition hover:opacity-90"
          >
            Post a wanted saddle
          </button>
          <a
            routerLink="/wanted"
            class="rounded-full border border-cream/40 px-6 py-3 text-sm font-medium text-cream transition hover:border-cream"
          >
            Browse wanted saddles
          </a>
        </div>

        <p class="mt-6 text-sm text-cream/55">Free to post. No listing fee, ever.</p>
      </div>
    </div>
  </div>
</section>

<!-- Trust strip -->
<section class="border-t border-cream/10 bg-forestdark text-cream">
  <div
    class="mx-auto grid max-w-7xl grid-cols-2 gap-x-4 gap-y-8 px-4 py-10 sm:gap-6 sm:px-6 sm:py-8 lg:grid-cols-4 lg:gap-8 lg:px-8"
  >
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="search" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">
          Be as specific as you like
        </p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">Or leave it open</p>
      </div>
    </div>
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="chat" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">
          Sellers respond directly
        </p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">Straight to your inbox</p>
      </div>
    </div>
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="shield" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">
          Same secure checkout
        </p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">Buyer protection still applies</p>
      </div>
    </div>
    <div
      class="flex flex-col items-center gap-2.5 text-center sm:flex-row sm:items-start sm:gap-3 sm:text-left"
    >
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream/10 text-cream sm:h-11 sm:w-11"
      >
        <app-icon name="dollar" svgClass="h-[18px] w-[18px]" [strokeWidth]="2" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold leading-snug text-cream text-balance">Free to post</p>
        <p class="mt-0.5 text-xs leading-snug text-cream/65">No cost, no obligation</p>
      </div>
    </div>
  </div>
</section>

<!-- How it works -->
<section class="mx-auto max-w-5xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8" appReveal>
  <p class="mb-3 text-center text-sm font-medium uppercase tracking-wide text-tandark">
    How it works
  </p>
  <h2 class="text-center font-display text-3xl font-semibold text-ink sm:text-4xl">
    The search, flipped
  </h2>

  <div class="mt-14 grid grid-cols-1 gap-10 sm:grid-cols-3 sm:gap-8">
    <div class="flex flex-col items-center text-center sm:items-start sm:text-left">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold font-display font-semibold text-ink"
        >1</span
      >
      <p class="mb-1 mt-4 font-semibold text-ink">Post what you're after</p>
      <p class="text-sm text-ink/70">
        Brand, discipline, size, tree width, budget — as specific or as open as you like. No photos
        needed.
      </p>
    </div>
    <div class="flex flex-col items-center text-center sm:items-start sm:text-left">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold font-display font-semibold text-ink"
        >2</span
      >
      <p class="mb-1 mt-4 font-semibold text-ink">Sellers with a match respond</p>
      <p class="text-sm text-ink/70">
        Anyone with a matching saddle already listed can reach out and start a conversation with you
        directly.
      </p>
    </div>
    <div class="flex flex-col items-center text-center sm:items-start sm:text-left">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold font-display font-semibold text-ink"
        >3</span
      >
      <p class="mb-1 mt-4 font-semibold text-ink">Buy the usual secure way</p>
      <p class="text-sm text-ink/70">
        Chat it through, then check out via that listing's Buy Now or Make an Offer — with the
        same buyer protection.
      </p>
    </div>
  </div>
</section>

<!-- Cross-sell: Shop / Sell -->
<section class="bg-creamdark/60 px-4 py-20 sm:px-6 sm:py-24 lg:px-8" appReveal>
  <div
    class="mx-auto grid max-w-6xl grid-cols-1 items-center overflow-hidden rounded-3xl border border-line bg-white lg:grid-cols-2"
  >
    <div class="aspect-[4/3] overflow-hidden lg:aspect-auto lg:h-full">
      <img
        src="assets/marketing/tackroom-panel.webp"
        alt="A saddle resting on a rack in a tack room"
        loading="lazy"
        class="h-full w-full object-cover"
      />
    </div>
    <div class="p-8 sm:p-12">
      <p class="text-sm font-medium uppercase tracking-wide text-tandark">
        Prefer to browse instead?
      </p>
      <h2 class="mt-3 font-display text-2xl font-semibold text-ink sm:text-3xl">
        Or explore what's already listed.
      </h2>
      <p class="mt-4 text-ink/70">
        Verified saddles are live right now — browse by discipline, size and price, or list your own
        in minutes.
      </p>
      <div class="mt-7 flex flex-wrap gap-3">
        <a
          routerLink="/shop"
          class="inline-flex items-center gap-1.5 rounded-full bg-forest px-5 py-2.5 text-sm font-medium text-cream transition hover:bg-forestdark"
        >
          Shop saddles
        </a>
        <a
          routerLink="/sell"
          class="inline-flex items-center gap-1.5 rounded-full border border-line px-5 py-2.5 text-sm font-medium text-ink transition hover:border-tan"
        >
          Sell a saddle
        </a>
      </div>
    </div>
  </div>
</section>

<!-- Final CTA -->
<section class="py-20 sm:py-24">
  <div class="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
    <h2 class="font-display text-3xl font-semibold text-ink sm:text-4xl">
      Ready to find your match?
    </h2>
    <p class="mt-4 text-ink/70">Takes about two minutes. Free to post, no listing fee, ever.</p>
    <button
      type="button"
      (click)="startWantedAd()"
      class="mt-8 inline-block cursor-pointer rounded-full bg-forest px-8 py-3.5 text-sm font-medium text-cream transition hover:bg-forestdark"
    >
      Post a wanted saddle
    </button>
  </div>
</section>
